import React, { Component } from 'react';
import Moment from 'moment';
import Typing from '../../conversations/components/Typing.jsx';
import Message from '../../conversations/components/Message';
import { GeckoModalService } from 'ui';
import { fetchConversationSnippet } from '../actions';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import GeckoLocation from 'services/gecko-location';
import { convertFromRaw } from 'draft-js';
import { isValueEditorObject } from 'utilities/editorObject';
import { CONSTS } from 'constants/index.js';
import './index.scss';
import ConversationEvent from "../../conversations/components/Message/ConversationEvent";

const editorObjectToPlainText = (editorObject) => {
  return convertFromRaw(editorObject).getPlainText();
};

const castToPlainText = (content) => {
  if (isValueEditorObject(content)) {
    return editorObjectToPlainText(content);
  }
  return content;
};

const ASSIGN_CONVERSATION = 'assign_conversation';
const ADD_CONTACT_NOTE = 'contact_note';
const ASSIGN_CONVERSAION_TAGS = 'conversation_tag';
const END_CONVERSATION_WITH_BOT = 'end_conversation_with_bot';
const SEND_MESSAGE = 'send_message';

class ConversationSnippet extends Component {
  constructor(props) {
    super(props);
    this.state = {
      bot: {},
      conversation: [],
      message: this.props.message,
      fetching: false,
      max: 3,
      snippetType: null,
      // bot skill types that use System Information UI
      assign_conversation: {
        sender: { additional: {}, type: 'agent' },
        sender_channel: {
          name: 'System Information',
          type: 'system_information',
        },
        content: `The conversation will be assigned to an agent.`,
      },
      conversation_tag: {
        sender: { additional: {}, type: 'agent' },
        sender_channel: {
          name: 'System Information',
          type: 'system_information',
        },
        content: `A tag will be assigned to the conversation.`,
      },
      contact_note: {
        sender: { additional: {}, type: 'agent' },
        sender_channel: {
          name: 'System Information',
          type: 'system_information',
        },
        content: null,
      },
    };
  }

  // gets a bot reply from an answerId
  fetchBotReply(answerId) {
    return this.fetchBotAnswer(answerId)
      .then((answer) => {
        if (answer.skill_id) {
          return this.fetchBotSkill(answer.skill_id).then((skill) => {
            return Promise.resolve({ answer, skill });
          });
        }
        return Promise.resolve(answer);
      })
      .catch((err) => {
        renderError(err);
      });
  }

  // gets a bot from a botId
  fetchBot(botId) {
    return GeckoRequest.get(`/bots/${botId}`)
      .then((bot) => bot)
      .catch((err) => {
        renderError(err);
      });
  }

  fetchBotAnswer(answerId, botId = this.props.botId) {
    return GeckoRequest.get(`/bots/${botId}/answers/${answerId}`)
      .then((answer) => answer)
      .catch((err) => {
        renderError(err);
      });
  }

  fetchBotSkill(skillId) {
    return GeckoRequest.get(`/bots/${this.props.botId}/skills/${skillId}`)
      .then((skill) => skill)
      .catch((err) => {
        renderError(err);
      });
  }

  fetchConversation(direction, numberOfResults, getLastBotMessage) {
    return fetchConversationSnippet({
      message: this.state.message,
      direction: direction,
      numberOfResults: numberOfResults,
      getLastBotMessage: getLastBotMessage,
      ignoreConversationEvents: true,
    })
      .then((conversation) => conversation)
      .catch((err) => {
        renderError(err);
      });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    // if props.answer_id is updated from parent dropdown, bot answer is updated
    if (nextProps.answerId !== this.props.answerId && nextProps.answerId) {
      this.setBotReply(nextProps.answerId);
      this.setState({ snippetType: 'answer' });
    }
  }

  UNSAFE_componentWillMount() {
    // sets the bot from the last-opened-bot Id
    this.fetchBot(this.props.botId)
      .then((bot) =>
        this.setState({ bot }, () => {
          // real user question - sample bot reply
          if (this.props.message && this.props.answerId && !this.props.answer) {
            // gets the message from props.message
            this.setConversation('equal', 1);
            this.setBotReply(this.props.answerId);
            this.setState({ snippetType: 'answer' });
            // real user question - conversation returned until bot replies
          } else if (
            this.props.message &&
            !this.props.answerId &&
            !this.props.answer
          ) {
            // gets the entire conversation until a bot replies from props.message
            this.setConversation('afterEqual', 2);
            this.setState({ snippetType: 'conversation' });
            // sample question - sample bot reply
          } else if (
            !this.props.message &&
            this.props.answerId &&
            this.props.answer
          ) {
            this.setSampleUserMessage();
            this.setBotReply(this.props.answerId);
          }
        })
      )
      .catch((err) => {
        renderError(err);
      });
  }

  // removes any existing bot reply from conversation list
  clearReply(removeType, keepType) {
    if (!this.state.conversation.length || !removeType || !keepType)
      return null;
    const doesReplyExist = this.state.conversation.find(
      (msg) => msg.type === removeType
    );
    if (doesReplyExist)
      this.setState({
        conversation: this.state.conversation.filter(
          (msg) => msg.type === keepType
        ),
      });
  }

  // adds recently fetched messages to the conversation
  handleNewlyLoadedMessages(messages) {
    const currentConversation = this.state.conversation;
    const newMessages = currentConversation.length
      ? currentConversation.concat(messages)
      : messages;
    if (newMessages.length)
      newMessages.sort(
        (msg_1, msg_2) => Moment(msg_1.received_at) - Moment(msg_2.received_at)
      );
    this.setState({ conversation: newMessages, fetching: false });
  }

  // adds a bot reply based on an answerId to the conversation thread
  setBotReply(answerId) {
    // removes any existing bot reply from conversation list
    this.clearReply('system', 'user');
    // gets the bot reply from the answerId
    this.fetchBotReply(answerId)
      .then((botReply) => {
        // gets the bots reply formated in json
        const botMessage = botReply.skill
          ? this.generateBotSkillReply(botReply.answer, botReply.skill)
          : this.generateBotAnswerReply(botReply);
        // adds to conversation
        return this.handleNewlyLoadedMessages(botMessage);
      })
      .catch((err) => {
        renderError(err);
      });
  }

  setSampleUserMessage() {
    const answer = this.props.answer;
    if (!answer) return null;
    const sampleQuestion =
      answer.samples[Math.floor(Math.random() * answer.samples.length)];
    const replyMessage = this.generateSampleUserMessage(sampleQuestion, answer);
    this.handleNewlyLoadedMessages(replyMessage);
  }

  // handles state.message passed to component
  setConversation(direction, numberOfResults = 1, getLastBotMessage = false) {
    if (!this.state.message || this.state.fetching) return null;
    this.setState({ fetching: true }, () => {
      this.fetchConversation(direction, numberOfResults, getLastBotMessage)
        .then((conversation) => {
          const fetchedConversation = conversation.data;
          this.handleNewlyLoadedMessages(fetchedConversation);
          // checks if message was a user question or bot reply
          const foundBotMessage = fetchedConversation.find(
            (msg) => msg.type === 'system'
          );
          // if bot message not found, no answerId passed to component and the last bot message hasnt been required yet
          if (!foundBotMessage && !this.props.answerId && !getLastBotMessage) {
            this.getNextConversationMessage(fetchedConversation);
          }
        })
        .catch((err) => {
          renderError(err);
        });
    });
  }

  getNextConversationMessage(conversation) {
    const mostRecentMessage = conversation[conversation.length - 1];
    // checks if the next message fetched explicitly be from the bot or not
    const getLastBotMessage =
      this.state.conversation.length === this.state.max ? true : false;
    this.setState({ message: mostRecentMessage }, () => {
      this.setConversation('after', 1, getLastBotMessage);
    });
  }

  getBotSenderDetails(skillType) {
    if (!skillType) return this.state.bot.user;
    if (this.state[skillType])
      return this.state[skillType].sender
        ? this.state[skillType].sender
        : this.state.bot.user;
    else return this.state.bot.user;
  }

  getBotChannelDetails(skillType) {
    if (!skillType) return { name: 'Gecko Chat', type: 'gecko_chat' };
    if (this.state[skillType]) return this.state[skillType].sender_channel;
    else return { name: 'Gecko Chat', type: 'gecko_chat' };
  }

  getBotMessageContentDetails(skillType, message) {
    if (!skillType && !message) return null;
    if (skillType === ASSIGN_CONVERSATION) {
      const agentOrTeam = message.includes('team') ? 'Team' : 'Agent'
      return `${agentOrTeam} was assigned`;
    }
    if (skillType === END_CONVERSATION_WITH_BOT) return 'Conversation was closed'
    if (skillType === ADD_CONTACT_NOTE) return `Note was added to contact`
    if (skillType === ASSIGN_CONVERSAION_TAGS) return 'Tag set on contact'
    if (message?.config?.message) {
      return castToPlainText(message.config.message);
    }
    if (message?.config?.confirm_message) {
      return castToPlainText(message.config.confirm_message);
    }
    return message;
  }

  getBotMessageEditorObjectDetails(skillType, message) {
    if (!skillType && !message) return null;
    if (
      message?.config?.message &&
      isValueEditorObject(message.config.message)
    ) {
      return message.config.message;
    }
    if (
      message?.config?.confirm_message &&
      isValueEditorObject(message.config.confirm_message)
    ) {
      return message.config.confirm_message;
    }
    return null;
  }

  generateDisplayListSkillReply(skill) {
    const sender = this.state.bot.user;
    const senderChannel = { name: 'Gecko Chat', type: 'gecko_chat' };
    const baseMessage = {
      skill_type: 'DISPLAY_LIST',
      type: 'system',
      received_at: Moment(),
      sender_id: sender.id ? sender.id : null,
      sender_channel: senderChannel,
      sender: sender,
      status: 'read',
    };
    return skill.config.messages.map((skillMessage, index) => {
      const isLastMessage = index === skill.config.messages.length - 1;
      return {
        ...baseMessage,
        id: `skill-${skill.id}-${index}`,
        config: isLastMessage ? skill.config : null,
        content: castToPlainText(skillMessage),
        editor_object: isValueEditorObject(skillMessage) ? skillMessage : null,
      };
    });
  };
  generateSuggestAnswerSkillReply(skill) {
    const sender = this.state.bot.user;
    const senderChannel = { name: 'Gecko Chat', type: 'gecko_chat' };
    const baseMessage = {
      skill_type: 'SUGGEST_ANSWER',
      type: 'system',
      received_at: Moment(),
      sender_id: sender.id ? sender.id : null,
      sender_channel: senderChannel,
      sender: sender,
      status: 'read',
    };
    return skill.config.messages.map((skillMessage, index) => {
      const isLastMessage = index === skill.config.messages.length - 1;
      return {
        ...baseMessage,
        id: `skill-${skill.id}-${index}`,
        config: isLastMessage ? skill.config : null,
        content: castToPlainText(skillMessage),
        editor_object: isValueEditorObject(skillMessage) ? skillMessage : null,
      }
    });
  };

  generateWorkflowSkillReply(skill) {
    const skillType = skill.outro_actions[0].type
    const sender = this.getBotSenderDetails(skillType);
    const channelType = this.getBotChannelDetails(skillType);
    const replyList = skillType === END_CONVERSATION_WITH_BOT ? [skill] :skill.outro_actions[0].value ;
    return replyList.map((message) => {
      const isSystemMessage = skillType !== SEND_MESSAGE
      return {
        content: this.getBotMessageContentDetails(skillType, message),
        editor_object: this.getBotMessageEditorObjectDetails(
          skillType,
          message
        ),
        config: message.config,
        id: `skill-${replyList.indexOf(message)}`,
        skill_type: skill.internal_skill,
        model: message.model,
        type: 'system',
        received_at: Moment(),
        sender_id: sender.id ? sender.id : null,
        sender_channel: channelType,
        sender: sender,
        status: 'read',
        isSystemMessage
      };
    }).filter(message => message !== null);
  }
  // generates the json required to display a bot skill
  generateBotSkillReply(answer, skill) {
    if (!this.state.bot || !answer || !skill) return null;
    if (skill.internal_skill === 'DISPLAY_LIST') {
      return this.generateDisplayListSkillReply(skill);
    }
    if (skill.internal_skill === 'SUGGEST_ANSWER') {
      return this.generateSuggestAnswerSkillReply(skill);
    }
    if(skill.internal_skill === 'WORKFLOW_ACTION') {
      return this.generateWorkflowSkillReply(skill)
    }
    const skillType = false;
    const sender = this.getBotSenderDetails(skillType);
    const channelType = this.getBotChannelDetails(skillType);
    const replyList = [skill];
    return replyList.map((message) => {
      return {
        content: this.getBotMessageContentDetails(skillType, message),
        editor_object: this.getBotMessageEditorObjectDetails(
          skillType,
          message
        ),
        config: message.config,
        id: `skill-${replyList.indexOf(message)}`,
        skill_type: skill.internal_skill,
        model: message.model,
        type: 'system',
        received_at: Moment(),
        sender_id: sender.id ? sender.id : null,
        sender_channel: channelType,
        sender: sender,
        status: 'read',
      };
    });
  }

  // generates the json required to display bot answer's
  generateBotAnswerReply(answer) {
    if (!this.state.bot || !answer || !answer.answers) return null;
    return answer.answers.map((ans) => {
      return {
        content: ans.content,
        editor_object: ans.editor_object,
        id: `answer-${answer.id}`,
        model: answer.model,
        type: 'system',
        received_at: Moment(),
        sender_id: this.props.botId,
        sender_channel: {
          name: 'Gecko Chat',
          type: 'gecko_chat',
        },
        sender: this.state.bot.user,
        status: 'read',
      };
    });
  }

  // generates the json required to display a sample of a users message
  generateSampleUserMessage(sampleQuestion, answer) {
    if (!sampleQuestion || !answer) return null;
    return [
      {
        content: sampleQuestion,
        config: answer.config,
        id: `sample-answer-${answer.id}`,
        model: answer.model,
        type: 'user',
        received_at: Moment(),
        sender_id: null,
        sender_channel: {
          name: 'Example',
          type: 'example',
        },
        sender: {
          full_name: 'Test User',
          fake_first_name: 'Test',
          fake_last_name: 'User',
          type: 'user',
          additional: {
            avatar_colour: '#560080',
          },
        },
        status: 'read',
      },
    ];
  }

  openContext(snippetType) {
    if (snippetType === 'conversation' && this.props.message.conversation_id) {
      const contactId = this.props.message.recipient_id
        ? this.props.message.recipient_id
        : this.props.message.sender_id;
      new GeckoModalService().close();
      return GeckoLocation.goTo(
        `/conversations/${this.props.message.conversation_id}`,
        { filter: CONSTS.FILTER_CONTACT, contact: contactId }
      );
    } else if (snippetType === 'answer' && this.props.answerId) {
      new GeckoModalService().close();
      return GeckoLocation.goTo(`answers/${this.props.answerId}`);
    }
  }

  render() {
    if (!this.state.conversation && !this.state.message) return <div></div>;
    const style = this.props.maxHeight
      ? { maxHeight: this.props.maxHeight + 'px' }
      : {};
    const iconStyle =
      this.state.snippetType && this.state.snippetType === 'answer'
        ? 'far fa-edit'
        : 'far fa-comments';
    const text =
      this.state.snippetType && this.state.snippetType === 'answer'
        ? `Edit ${this.state.snippetType}`
        : `View ${this.state.snippetType}`;
    return (
      <div className="ConversationSnippet-wrapper" style={style}>
        {this.state.snippetType ? (
          <div
            className="ConversationSnippet-button"
            onClick={() => this.openContext(this.state.snippetType)}
          >
            <i className={'gecko-icon ' + iconStyle}></i> <span>{text}</span>
          </div>
        ) : (
          ''
        )}
        <div className="conversation-snippet-messages-container">
          {this.state.conversation.length ? (
            this.state.conversation.map((msg) => {
              if(msg.isSystemMessage) {
                return <ConversationEvent message={msg}/>
              }
              return <Message key={msg.id} message={msg} />;
            })
          ) : this.state.conversation.id ? (
            <Message
              key={this.state.conversation.id}
              message={this.state.conversation}
            />
          ) : (
            <div className="col-10">
              <div style={{ paddingLeft: '20px' }}>
                <Typing />
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}

export default ConversationSnippet;
